<template>
  <!-- Overlay for mobile -->
  <div class="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 lg:hidden"
       :class="{'opacity-100 pointer-events-auto': modelValue, 'opacity-0 pointer-events-none': !modelValue}"
       @click="$emit('update:modelValue', false)"></div>

  <!-- Drawer -->
  <div
      ref="drawerRef"
      :class="[
      'fixed top-0 left-0 h-full w-17/20 max-w-96 overflow-y-auto bg-background min-h-screen shadow-lg z-50',
      'touch-action-pan-y',
      'transform -translate-x-full lg:translate-x-0 transition-transform duration-300 ease-in-out', // Base state: closed and ready to transition
      { 'translate-x-0': modelValue }, // Open state: overrides the base translate
      { '!transition-none': isDragging }, // Drag state: disables smooth transition
      'lg:w-96 lg:relative lg:shadow-none lg:!transform-none lg:z-auto lg:shrink-0', // Desktop styles
    ]"
      :style="{ transform: x !== null ? `translateX(${x}px)` : '' }"
  >
    <div class="flex items-center gap-3 p-4">
      <img src="/icons/icon-192x192.png" alt="Forecaster Logo" class="w-8 h-8" />
      <h1 class="text-xl text-primary font-semibold">Forecaster<span class="text-secondary text-lg">.dev</span></h1>
      <div @click="$emit('update:modelValue', false)" class="ml-auto p-2 rounded-full hover:bg-hover lg:hidden">
        <IconClose class="text-lg" />
      </div>
    </div>
    <div class="flex flex-col h-full">
      <nav class="flex-1">
        <md-list class="bg-transparent">
          <!-- Current location -->
          <md-list-item
              type="button"
              @click="router.push({ name: 'Home' })"
              :selected="route.name === 'Home'"
          >
            <md-icon slot="start"><IconLocationOn /></md-icon>
            <div slot="headline">{{ $t('drawer.currentLocation') }}</div>
          </md-list-item>

          <div class="py-2">
            <md-divider></md-divider>
          </div>
          
          <div class="px-4 py-2 text-sm font-semibold text-secondary">{{ $t('drawer.starredLocations')}}</div>
          
          <template v-if="placesStore.starredPlaces.length > 0">
            <md-list-item
                v-for="place in placesStore.starredPlaces"
                :key="place.lat + '-' + place.lon"
                type="button"
                @click="router.push({ name: 'LocationDetail', params: { lat: Number(place.lat).toFixed(3), lng: Number(place.lon).toFixed(3) }, query: { name: place.name } })"
                :selected="route.name === 'LocationDetail' && route.params.lat === Number(place.lat).toFixed(3) && route.params.lng === Number(place.lon).toFixed(3)"
            >
              <md-icon slot="start"><IconStar /></md-icon>
              <div slot="headline">{{ place.name }}</div>
              <md-icon
                  slot="end"
                  @click.stop="removeStarredPlace(place)"
                  class="ml-2"
              >
                <IconClose />
              </md-icon>
            </md-list-item>
          </template>
          <div v-else class="px-6 py-3 text-sm text-center text-secondary">{{ $t('drawer.starToSave') }}</div>

          <div class="py-2">
            <md-divider></md-divider>
          </div>

          <md-list-item
              type="button"
              @click="router.push({ name: 'Settings' })"
              :selected="route.name === 'Settings'"
          >
            <md-icon slot="start"><IconSettings /></md-icon>
            <div slot="headline">{{ $t('drawer.settings') }}</div>
          </md-list-item>

          <md-list-item
              type="button"
              @click="router.push({ name: 'ColorGuide' })"
              :selected="route.name === 'ColorGuide'"
          >
            <md-icon slot="start"><IconHelpOutline /></md-icon>
            <div slot="headline">{{ $t('drawer.colorGuide') }}</div>
          </md-list-item>

          <md-list-item
              type="button"
              @click="router.push({ name: 'Feedback' })"
              :selected="route.name === 'Feedback'"
          >
            <md-icon slot="start"><IconComment /></md-icon>
            <div slot="headline">{{ $t('drawer.feedback') }}</div>
          </md-list-item>
        </md-list>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePlacesStore } from '../stores/places';
import { computed, watch, ref } from 'vue';
import { useDrag } from '@vueuse/gesture';

// Material Web Imports
import '@material/web/list/list.js';
import '@material/web/list/list-item.js';
import '@material/web/divider/divider.js';
import '@material/web/icon/icon.js';
import '@material/web/iconbutton/icon-button.js';

import IconClose from '~icons/material-symbols/close-rounded';
import IconLocationOn from '~icons/material-symbols/location-on';
import IconStar from '~icons/material-symbols/star';
import IconSettings from '~icons/material-symbols/settings';
import IconHelpOutline from '~icons/material-symbols/help-outline';
import IconComment from '~icons/material-symbols/comment';

const router = useRouter();
const route = useRoute();
const placesStore = usePlacesStore();
const { t } = useI18n();

const props = defineProps({
  modelValue: Boolean,
});

const emit = defineEmits(['update:modelValue']);

const drawerRef = ref(null);
// Initialize x to null. The inline style will only be applied when dragging.
const x = ref(null);
const isDragging = ref(false);

// Watch for route changes to close the drawer on mobile
watch(route, () => {
  if (props.modelValue) {
    emit('update:modelValue', false);
  }
});

// When the drawer is closed (by any means), reset the inline transform
// so the CSS classes can take over again.
watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    x.value = null;
  }
});

useDrag(
    ({ active, movement: [mx], last, cancel }) => {
      // This guard is important, it prevents dragging the closed drawer into view
      if (!props.modelValue && active) {
        cancel()
        return
      }

      isDragging.value = active;

      if (last) { // On drag release
        const drawerWidth = drawerRef.value.clientWidth;
        if (mx < -drawerWidth * 0.2) {
          emit('update:modelValue', false);
        } else {
          // Snap back to open position by resetting the inline style.
          // The `translate-x-0` class will keep it open.
          x.value = 0;
        }
      } else { // During drag
        // Apply inline style, clamping at 0 to prevent over-dragging to the right.
        x.value = Math.min(0, mx);
      }
    },
    {
      domTarget: drawerRef,
      axis: 'x',
      filterTaps: true,
    }
);

function removeStarredPlace(place) {
  placesStore.removePlace(place);
}
</script>
<style scoped>
  md-list-item {
    margin: 2px 8px;
    width: calc(100% - 16px);
  }
</style>